import { X, TrendingUp, TrendingDown, Minus, Shield, Globe, Zap, AlertTriangle } from 'lucide-react'
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import type { EscalationData } from '@/lib/api'

interface Props {
  data: EscalationData
  onClose: () => void
}

const LEVEL_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  elevated: '#eab308',
  normal: '#22c55e',
}

const BAR_COLOR = (v: number) =>
  v >= 25 ? '#ef4444' : v >= 15 ? '#f97316' : v >= 8 ? '#eab308' : '#22c55e'

const INSTABILITY_COLOR = (v: number) =>
  v >= 60 ? '#ef4444' : v >= 30 ? '#f97316' : '#eab308'

export default function EscalationDetailPanel({ data, onClose }: Props) {
  const levelColor = LEVEL_COLOR[data.level] ?? '#22c55e'
  const pct = Math.min(data.composite_score, 100)
  const circumference = 2 * Math.PI * 52
  const offset = circumference - (pct / 100) * circumference

  const TrendIcon =
    data.cyber_escalation.trend === 'rising' ? TrendingUp
    : data.cyber_escalation.trend === 'falling' ? TrendingDown
    : Minus

  // Cyber sub-components for bar chart (scored 0-max each)
  const cyberBars = [
    { label: 'KEV Spike',      key: 'kev_spike',             value: data.cyber_escalation.kev_spike,              max: 30, desc: 'Known exploited vulnerabilities added in past 24h' },
    { label: 'Ransomware',     key: 'ransomware_activity',   value: data.cyber_escalation.ransomware_activity,    max: 30, desc: 'Active ransomware campaigns detected' },
    { label: 'IOC Surge',      key: 'ioc_surge',             value: data.cyber_escalation.ioc_surge,              max: 25, desc: 'Indicators of compromise published' },
    { label: 'APT Chatter',    key: 'apt_chatter',           value: data.cyber_escalation.apt_chatter,            max: 15, desc: 'Advanced persistent threat activity signals' },
  ]

  // Use the backend's clamped global_score (0-100) as the authoritative cyber total
  const cyberTotal = data.cyber_escalation.global_score ?? cyberBars.reduce((s, b) => s + b.value, 0)
  const geoTotal = data.geo_score ?? 0

  // Radar data for score composition
  const radarData = [
    { subject: 'KEV',       value: data.cyber_escalation.kev_spike,            fullMark: 30 },
    { subject: 'Ransomware',value: data.cyber_escalation.ransomware_activity,   fullMark: 30 },
    { subject: 'IOC',       value: data.cyber_escalation.ioc_surge,             fullMark: 25 },
    { subject: 'APT',       value: data.cyber_escalation.apt_chatter,           fullMark: 15 },
    { subject: 'Geo',       value: Math.min(geoTotal, 100),                     fullMark: 100 },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-gray-950 border border-gray-800 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 bg-gray-950/95 border-b border-gray-800 backdrop-blur">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4" style={{ color: levelColor }} />
            <span className="text-sm font-semibold text-white font-mono tracking-wide uppercase">Escalation Analysis</span>
            <span
              className="text-xs px-2 py-0.5 rounded-full font-mono uppercase font-bold"
              style={{ color: levelColor, backgroundColor: `${levelColor}20`, border: `1px solid ${levelColor}40` }}
            >
              {data.level}
            </span>
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white transition-colors rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Score + Radar row */}
          <div className="grid grid-cols-2 gap-5">
            {/* Big gauge */}
            <div className="flex flex-col items-center justify-center bg-gray-900/60 rounded-xl p-5 border border-gray-800">
              <div className="relative w-32 h-32">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="6" />
                  <circle
                    cx="60" cy="60" r="52" fill="none"
                    stroke={levelColor}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ filter: `drop-shadow(0 0 8px ${levelColor}80)`, transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold font-mono" style={{ color: levelColor }}>{Math.round(pct)}</span>
                  <span className="text-[10px] text-gray-500 font-mono uppercase tracking-widest">/100</span>
                </div>
              </div>
              <div className="mt-3 text-center space-y-1">
                <div className="flex items-center gap-1.5 justify-center text-xs text-gray-400 font-mono">
                  <TrendIcon className="w-3.5 h-3.5" style={{ color: levelColor }} />
                  <span>Cyber trend: <span style={{ color: levelColor }}>{data.cyber_escalation.trend}</span></span>
                </div>
                <p className="text-[10px] text-gray-600 font-mono">Score = cyber + geo instability</p>
              </div>
            </div>

            {/* Radar */}
            <div className="bg-gray-900/60 rounded-xl p-4 border border-gray-800">
              <p className="text-[10px] text-gray-500 font-mono uppercase tracking-widest mb-2">Signal Composition</p>
              <ResponsiveContainer width="100%" height={160}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius={60}>
                  <PolarGrid stroke="#1e293b" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#6b7280', fontSize: 10, fontFamily: 'monospace' }} />
                  <Radar name="Score" dataKey="value" stroke={levelColor} fill={levelColor} fillOpacity={0.15} strokeWidth={1.5} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Methodology banner */}
          <div className="bg-blue-950/30 border border-blue-800/30 rounded-lg px-4 py-3 text-xs text-gray-400 font-mono leading-relaxed">
            <span className="text-blue-400 font-semibold">How it's calculated: </span>
            The composite score (0–100) combines a <span className="text-white">cyber sub-score</span> (max 100 pts from KEV spikes, ransomware campaigns, IOC surges, APT chatter, and critical event volume — each normalised against a 7-day baseline) and a <span className="text-white">geopolitical sub-score</span> (max 100 pts from country conflict density, infrastructure outages, and event frequency — weighted by severity). Both are blended and clamped to 0–100.
          </div>

          {/* Cyber breakdown bar chart */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-3.5 h-3.5 text-red-400" />
              <span className="text-xs font-semibold text-gray-300 font-mono uppercase tracking-wider">Cyber Threat Breakdown</span>
              <span className="ml-auto text-xs text-gray-500 font-mono">sub-total: <span className="text-white">{cyberTotal.toFixed(1)}</span></span>
            </div>
            <div className="space-y-2">
              {cyberBars.map((b) => (
                <div key={b.key}>
                  <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
                    <span className="text-gray-400">{b.label}</span>
                    <span className="text-gray-300">{b.value.toFixed(1)} / {b.max}</span>
                  </div>
                  <div className="relative h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${(b.value / b.max) * 100}%`,
                        backgroundColor: BAR_COLOR(b.value),
                        boxShadow: b.value >= 15 ? `0 0 6px ${BAR_COLOR(b.value)}80` : 'none',
                      }}
                    />
                  </div>
                  <p className="text-[9px] text-gray-600 font-mono mt-0.5">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Country instability */}
          {data.country_scores.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-xs font-semibold text-gray-300 font-mono uppercase tracking-wider">Country Instability Signals</span>
                <span className="ml-auto text-[10px] text-gray-500 font-mono">{data.country_scores.length} countries tracked</span>
              </div>

              {/* Bar chart for top 10 */}
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={data.country_scores.slice(0, 10).map(c => ({
                    name: c.country_code,
                    score: Math.round(c.instability_score),
                    conflict: Math.round(c.conflict_density),
                    cyber: Math.round(c.cyber_density),
                  }))}
                  margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                >
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10, fontFamily: 'monospace' }} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 9 }} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 6, fontSize: 11, fontFamily: 'monospace' }}
                    labelStyle={{ color: '#e2e8f0' }}
                    formatter={(v: number, name: string) => [v, name]}
                  />
                  <Bar dataKey="score" name="Instability" radius={[3, 3, 0, 0]}>
                    {data.country_scores.slice(0, 10).map((c, i) => (
                      <Cell key={i} fill={INSTABILITY_COLOR(c.instability_score)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Detailed table */}
              <div className="mt-3 border border-gray-800/60 rounded-lg overflow-hidden">
                <table className="w-full text-[10px] font-mono">
                  <thead>
                    <tr className="bg-gray-900 text-gray-500 uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Country</th>
                      <th className="text-right px-2 py-2">Instability</th>
                      <th className="text-right px-2 py-2">Conflict</th>
                      <th className="text-right px-2 py-2">Cyber</th>
                      <th className="text-right px-2 py-2">Outages</th>
                      <th className="text-right px-3 py-2">Events 24h</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.country_scores.slice(0, 10).map((c, i) => (
                      <tr key={c.country_code} className={i % 2 === 0 ? 'bg-gray-900/30' : ''}>
                        <td className="px-3 py-1.5 text-gray-300">{c.country_name || c.country_code}</td>
                        <td className="text-right px-2 py-1.5 font-bold" style={{ color: INSTABILITY_COLOR(c.instability_score) }}>
                          {c.instability_score.toFixed(0)}
                        </td>
                        <td className="text-right px-2 py-1.5 text-gray-400">{c.conflict_density.toFixed(1)}</td>
                        <td className="text-right px-2 py-1.5 text-gray-400">{c.cyber_density.toFixed(1)}</td>
                        <td className="text-right px-2 py-1.5 text-gray-400">{c.infra_outages.toFixed(1)}</td>
                        <td className="text-right px-3 py-1.5 text-gray-400">{c.event_count_24h}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Score decomposition summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Zap className="w-3 h-3 text-red-400" />
                <span className="text-[10px] text-gray-400 font-mono uppercase">Cyber Sub-score</span>
              </div>
              <span className="text-2xl font-bold font-mono text-red-400">{cyberTotal.toFixed(1)}</span>
              <span className="text-[10px] text-gray-600 font-mono"> / 100</span>
            </div>
            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Globe className="w-3 h-3 text-orange-400" />
                <span className="text-[10px] text-gray-400 font-mono uppercase">Geo Sub-score</span>
              </div>
              <span className="text-2xl font-bold font-mono text-orange-400">{geoTotal.toFixed(1)}</span>
              <span className="text-[10px] text-gray-600 font-mono"> pts contribution</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
